import { CheckCircle2, Circle, FileQuestion, PlayCircle, ScrollText, Stethoscope } from 'lucide-react';
import { Link, useParams } from 'react-router';
import { Markdown } from '@/components/Markdown';
import { Alert, Badge, ButtonLink, PageHeader, ProgressBar, QueryError, Spinner } from '@/components/ui';
import { formatDate } from '@/lib/utils';
import type { ModuleKind } from '@/types/db';
import { useCourse, useMyProgress } from './api';

const KIND_ICON: Record<ModuleKind, typeof ScrollText> = {
  lesson: ScrollText,
  video: PlayCircle,
  quiz: FileQuestion,
  case_study: Stethoscope,
  document: ScrollText,
};

export function CoursePage() {
  const { courseId } = useParams();
  const { data, isPending, isError, error } = useCourse(courseId);
  const progress = useMyProgress();

  if (isPending) return <Spinner />;
  if (isError) return <QueryError error={error} />;

  const { course, modules } = data;
  const done = new Set(progress.data?.modules.filter((m) => m.course_id === course.id).map((m) => m.module_id));
  const completion = progress.data?.completions.find((c) => c.course_id === course.id);
  const required = modules.filter((m) => m.required);
  const requiredDone = required.filter((m) => done.has(m.id)).length;
  const next = modules.find((m) => !done.has(m.id));

  return (
    <div>
      <PageHeader
        back={{ to: '/learn?tab=catalog', label: 'Courses' }}
        title={course.title}
        subtitle={course.description}
        actions={next && !completion && <ButtonLink to={`/learn/courses/${course.id}/modules/${next.id}`}>{done.size > 0 ? 'Continue' : 'Start course'}</ButtonLink>}
      />
      <div className="mb-6 flex flex-wrap gap-1.5">
        {course.ce_hours > 0 && <Badge tone="brand">{course.ce_hours} CE hours</Badge>}
        {course.estimated_minutes && <Badge tone="neutral">{course.estimated_minutes} minutes</Badge>}
        {course.is_sample && <Badge tone="warning">Sample course</Badge>}
      </div>
      {completion && (
        <Alert tone="success" title="Course completed" className="mb-4">
          Completed {formatDate(completion.completed_at)}
          {completion.score !== null && ` with a score of ${Math.round(Number(completion.score))}%`}.
        </Alert>
      )}
      <div className="mb-8 max-w-md">
        <div className="mb-2 flex justify-between text-sm">
          <span className="label-caps text-stone-500 dark:text-stone-400">Progress</span>
          <span className="font-mono text-xs text-stone-500">
            {requiredDone} / {required.length} required
          </span>
        </div>
        <ProgressBar value={required.length ? requiredDone / required.length : 0} />
      </div>
      <ol className="divide-y divide-stone-200 border-y border-stone-200 dark:divide-stone-800 dark:border-stone-800">
          {modules.map((m, i) => {
            const Icon = KIND_ICON[m.kind];
            return (
              <li key={m.id}>
                <Link to={`/learn/courses/${course.id}/modules/${m.id}`} className="flex items-center gap-3 px-1 py-3 transition-colors hover:bg-stone-100 dark:hover:bg-ink-900">
                  {done.has(m.id) ? <CheckCircle2 className="size-4 text-emerald-600" /> : <Circle className="size-4 text-stone-300 dark:text-stone-600" />}
                  <span className="w-6 font-mono text-xs text-stone-400">{String(i + 1).padStart(2, '0')}</span>
                  <Icon className="size-4 text-stone-400" />
                  <span className="flex-1 font-medium">{m.title}</span>
                  {!m.required && <Badge tone="neutral">Optional</Badge>}
                  {m.estimated_minutes && <span className="font-mono text-xs text-stone-500">{m.estimated_minutes} min</span>}
                </Link>
              </li>
            );
          })}
      </ol>
      {modules.length === 0 && <Markdown className="mt-4">{'_This course has no modules yet._'}</Markdown>}
    </div>
  );
}
